import type React from 'react';
import { createContext, memo, useCallback, useContext } from 'react';
import { v1 as uuid } from 'uuid';

import {
  DecisionStatusEnum,
  ORDER_CANCEL_REQUEST,
  ORDER_CONTROL_REQUEST,
  OrderControlActionEnum,
  POST,
  PUT,
  PricingMode,
  formattedDateForSubscription,
  frontendAllocationsToBackendAllocations,
  request,
  useGlobalToasts,
  useSecuritiesContext,
  useSocketClient,
  useUserContext,
} from '@talos/kyoko';

import { useOrderService } from '@src/components/OMS/NewOrder/OrderService';
import { shouldSendDurationOnModify } from '@src/components/OMS/NewOrder/utils';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useIsMobileLayout } from '@src/hooks/useIsMobileLayout';
import { DatadogTradingAction, trackOrderAction } from '@src/utils/datadogTradingActions';
import { assertOrderCurrency, cleanParametersForOrder } from '@src/utils/order';
import { resolveUiRequestSource } from '@src/utils/resolveUiRequestSource';

import type {
  FullOrderRequest,
  ModifyOrderArgs,
  ModifyOrderRequest,
  OrderInteractionRequestOptions,
  OrderInteractionsContextProps,
  SendOrderArgs,
} from './orders.types';

const OrderInteractions = createContext<OrderInteractionsContextProps | undefined>(undefined);
OrderInteractions.displayName = 'OrderInteractionsContext';

export const useOrderInteractions = () => {
  const context = useContext(OrderInteractions);
  if (context === undefined) {
    throw new Error('Missing OrderInteractionsContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
};

export const OrderInteractionsProvider = memo(function OrderInteractionsProvider(
  props: React.PropsWithChildren<unknown>
) {
  const client = useSocketClient();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { orgApiEndpoint } = useUserContext();
  const { enableDurationSubmission } = useFeatureFlag();
  const isMobileLayout = useIsMobileLayout();
  const { add: addToast } = useGlobalToasts();
  const orderService = useOrderService({ addToast });

  const send = useCallback(
    (
      {
        symbol,
        side,
        orderQty,
        group,
        price,
        pricingMode,
        parameters,
        marketAccounts,
        subAccountAllocations,
        clOrdID,
        ordType,
        timeInForce,
        selectedStrategy,
        subAccounts,
        allocationValueType,
        useTradeAllocations,
        subAccount,
        orderCurrency,
        expectedFillPrice,
        expectedFillQty,
        comment,
        legParams,
        initialDecisionStatus,
        requestSource: explicitRequestSource,
        ...rest
      }: SendOrderArgs,
      requestOptions: OrderInteractionRequestOptions = {}
    ): boolean => {
      const _typecheck: EmptyObject = rest;
      const modifiedParameters = cleanParametersForOrder({
        parameters,
        selectedStrategy,
        enableDurationSubmission: enableDurationSubmission || initialDecisionStatus === DecisionStatusEnum.Staged,
      });
      const data: FullOrderRequest = {
        Symbol: symbol,
        ClOrdID: clOrdID,
        Side: side,
        TransactTime: formattedDateForSubscription(new Date()),
        OrderQty: orderQty,
        OrdType: ordType as string, // This is weird. Should the request type have the OrdType enum? when does the enum become a string?
        Price: price,
        TimeInForce: timeInForce,
        Markets: marketAccounts,
        Parameters: modifiedParameters,
        ExpectedFillPrice: expectedFillPrice,
        ExpectedFillQty: expectedFillQty,
        Comments: comment,
        LegParams: legParams,
      };
      if (group) {
        data.Group = group;
      }
      if (selectedStrategy) {
        data.Strategy = selectedStrategy.Name;
      }
      if (orderCurrency) {
        data.Currency = orderCurrency;
      }
      if (initialDecisionStatus) {
        data.InitialDecisionStatus = initialDecisionStatus;
      }
      // Only include PricingMode when it differs from Default
      if (pricingMode && pricingMode !== PricingMode.Default) {
        data.PricingMode = pricingMode;
      }
      if (subAccounts && subAccounts?.length > 0) {
        if (useTradeAllocations) {
          const preparedTradeAllocations = frontendAllocationsToBackendAllocations({
            subAccountAllocations,
            subAccounts,
            allocationValueType,
            quantity: orderQty,
          });
          data.Allocation = preparedTradeAllocations;
        } else if (subAccountAllocations?.length === 1) {
          data.SubAccount = subAccountAllocations[0].subAccount;
        } else if (subAccount) {
          // Click to Trade
          data.SubAccount = subAccount;
        }
      }

      const requestSource = resolveUiRequestSource({
        isMobileLayout,
        explicit: explicitRequestSource,
      });
      if (requestSource) {
        data.RequestSource = requestSource;
      }

      if (!assertOrderCurrency(data, securitiesBySymbol.get(data.Symbol)!)) {
        return false;
      }

      orderService.requestOrder(data, requestOptions);
      return true;
    },
    [securitiesBySymbol, enableDurationSubmission, isMobileLayout, orderService]
  );

  const modify = useCallback(
    (
      {
        symbol,
        price,
        orderID,
        clOrdID,
        orderQty,
        marketAccounts,
        subAccountAllocations,
        parameters,
        group,
        selectedStrategy,
        subAccounts,
        allocationValueType,
        useTradeAllocations,
        subAccount,
        comment,
        legParams,
        decisionStatus,
      }: ModifyOrderArgs,
      requestOptions: OrderInteractionRequestOptions = {}
    ) => {
      const data: ModifyOrderRequest = {
        Symbol: symbol,
        OrderID: orderID,
        ClOrdID: clOrdID,
        TransactTime: formattedDateForSubscription(new Date()),
      };

      if (parameters) {
        const modifiedParameters = selectedStrategy
          ? cleanParametersForOrder({
              parameters,
              selectedStrategy,
              enableDurationSubmission: shouldSendDurationOnModify({
                decisionStatus,
                enableDurationSubmission,
              }),
            })
          : parameters;

        data.Parameters = modifiedParameters;
      }
      if (price != null) {
        data.Price = price;
      }
      if (orderQty != null) {
        data.OrderQty = orderQty;
      }
      if (marketAccounts != null) {
        data.Markets = marketAccounts;
      }
      if (selectedStrategy) {
        data.Strategy = selectedStrategy.Name;
      }
      if (group != null) {
        data.Group = group;
      }
      if (comment != null) {
        data.Comments = comment;
      }
      if (legParams != null) {
        data.LegParams = legParams;
      }
      if (subAccounts && subAccounts?.length > 0) {
        if (useTradeAllocations) {
          const preparedTradeAllocations = frontendAllocationsToBackendAllocations({
            subAccountAllocations,
            subAccounts,
            allocationValueType,
            quantity: orderQty,
          });
          data.Allocation = preparedTradeAllocations;
        } else if (subAccountAllocations?.length === 1) {
          data.SubAccount = subAccountAllocations[0].subAccount;
        } else if (subAccount) {
          // Click to Trade
          data.SubAccount = subAccount;
        }
      }
      // Safeguard against poorly formatted or not required SubAccount selection
      if (data.SubAccount == null || data.SubAccount === '') {
        delete data.SubAccount;
      }

      orderService.updateOrder(data, requestOptions);
    },
    [enableDurationSubmission, orderService]
  );

  const cancel = useCallback(
    (orderID?: string) => {
      const clOrdID = uuid();
      trackOrderAction(DatadogTradingAction.Cancel, { clOrdID, orderID });
      client.registerPublication({
        type: ORDER_CANCEL_REQUEST,
        data: [
          {
            ClOrdID: clOrdID,
            OrderID: orderID,
            TransactTime: formattedDateForSubscription(new Date()),
          },
        ],
      });
    },
    [client]
  );

  const pause = useCallback(
    (orderID?: string) => {
      const clOrdID = uuid();
      trackOrderAction(DatadogTradingAction.Pause, { clOrdID, orderID });
      client.registerPublication({
        type: ORDER_CONTROL_REQUEST,
        data: [
          {
            ClOrdID: clOrdID,
            OrderID: orderID,
            Action: OrderControlActionEnum.Pause,
          },
        ],
      });
    },
    [client]
  );

  const resume = useCallback(
    (orderID?: string) => {
      const clOrdID = uuid();
      trackOrderAction(DatadogTradingAction.Resume, { clOrdID, orderID });
      client.registerPublication({
        type: ORDER_CONTROL_REQUEST,
        data: [
          {
            ClOrdID: clOrdID,
            OrderID: orderID,
            Action: OrderControlActionEnum.Resume,
          },
        ],
      });
    },
    [client]
  );

  const archive = useCallback(
    (orderID: string) => request(POST, `${orgApiEndpoint}/orders/${orderID}/archive`),
    [orgApiEndpoint]
  );

  const unarchive = useCallback(
    (orderID: string) => request(POST, `${orgApiEndpoint}/orders/${orderID}/unarchive`),
    [orgApiEndpoint]
  );

  const forceCancel = useCallback(
    (orderID: string) => request(PUT, `${orgApiEndpoint}/orders/${orderID}/force-cancel`),
    [orgApiEndpoint]
  );

  return (
    <OrderInteractions.Provider value={{ send, modify, cancel, pause, resume, archive, unarchive, forceCancel }}>
      {props.children}
    </OrderInteractions.Provider>
  );
});
